import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PublishIcon from '@mui/icons-material/Publish';
import { scheduledPostsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { usePublishStatusPolling } from '../hooks/usePublishStatusPolling';
import {
  formatApiError,
  getPostPublishError,
} from '../utils/postPublishErrors';
import { formatSchedulingTime } from '../utils/datetime';
import { DataTable, StatusChip } from './primitives';

function canPublishNow(post) {
  const ps = (post.publish_status || '').toLowerCase();
  const st = (post.status || '').toLowerCase();
  if (st === 'published' || ps === 'published') return false;
  if (ps === 'publishing') return false;
  return Boolean(post.is_active !== false);
}

/** Legacy `/posts/scheduled` queue — embedded in Scheduling hub (phase 5.1). */
export default function ScheduledPostsQueue() {
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [form, setForm] = useState({
    platform_account_id: '',
    content: '',
    content_type: 'text',
    scheduled_time: '',
    timezone: 'EST',
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(
    async (options = {}) => {
      if (!options.silent) setLoading(true);
      try {
        const data = await scheduledPostsAPI.list();
        setPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setPosts([]);
        if (!options.silent) {
          showError(formatApiError(err, 'Failed to load scheduled posts'));
        }
      } finally {
        if (!options.silent) setLoading(false);
      }
    },
    [showError],
  );

  usePublishStatusPolling({
    posts,
    refreshPosts: fetchPosts,
    showError,
  });

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleClose = () => {
    setOpen(false);
    setForm({
      platform_account_id: '',
      content: '',
      content_type: 'text',
      scheduled_time: '',
      timezone: 'EST',
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await scheduledPostsAPI.create(form);
      await fetchPosts();
      handleClose();
      showSuccess('Post scheduled');
    } catch (err) {
      showError(formatApiError(err, 'Failed to schedule post'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishNow = async (post) => {
    if (!post?.id || publishingId) return;
    setPublishingId(post.id);
    try {
      // Move due time to now so Celery Beat / scan picks it up immediately.
      await scheduledPostsAPI.update(post.id, {
        platform_account_id: post.platform_account_id,
        content: post.content || '',
        content_type: post.content_type || 'text',
        media_urls: post.media_urls || null,
        hashtags: post.hashtags || null,
        mentions: post.mentions || null,
        location: post.location || null,
        scheduled_time: new Date().toISOString(),
        timezone: post.timezone || 'EST',
        platform_specific: post.platform_specific || null,
      });
      showSuccess('Queued for immediate publish');
      await fetchPosts();
    } catch (err) {
      showError(formatApiError(err, 'Failed to queue publish now'));
    } finally {
      setPublishingId(null);
    }
  };

  const renderStatusCell = (post) => {
    const err = getPostPublishError(post);
    const ps = (post.publish_status || '').toLowerCase();
    const hasPipelineStatus =
      post.status === 'failed' ||
      ps === 'failed' ||
      post.status === 'published' ||
      ps === 'published' ||
      ps === 'publishing' ||
      ps === 'queued' ||
      post.status === 'scheduled';
    return (
      <>
        {hasPipelineStatus ? (
          <StatusChip post={post} />
        ) : (
          <StatusChip status={post.is_active ? 'active' : 'inactive'} />
        )}
        {err ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Tooltip title={err}>
              <ErrorOutlineIcon color="error" fontSize="small" />
            </Tooltip>
            <Typography
              variant="caption"
              color="error"
              sx={{ maxWidth: 240, wordBreak: 'break-word' }}
            >
              {err}
            </Typography>
          </Box>
        ) : null}
      </>
    );
  };

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Simple queue backed by <code>/posts/scheduled</code>. For calendar, bulk,
        and recurring workflows use the other tabs on this page.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpen(true)}
        sx={{ mb: 2, minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
      >
        + Schedule Post
      </Button>

      {isMobile ? (
        <Box
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {loading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : !posts.length ? (
            <Typography color="text.secondary">
              No scheduled posts in the queue.
            </Typography>
          ) : (
            posts.map((post) => (
              <Card key={post.id} variant="outlined" sx={{ maxWidth: '100%' }}>
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {(post.content || '').slice(0, 120)}
                      {(post.content || '').length > 120 ? '…' : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Due: {formatSchedulingTime(post.scheduled_time)}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      <Chip
                        size="small"
                        label={`Account #${post.platform_account_id}`}
                      />
                      <Chip size="small" label={post.content_type || 'text'} />
                    </Stack>
                    {renderStatusCell(post)}
                    {canPublishNow(post) && (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<PublishIcon />}
                        disabled={publishingId === post.id}
                        onClick={() => handlePublishNow(post)}
                        sx={{ minHeight: 44, textTransform: 'none' }}
                      >
                        {publishingId === post.id
                          ? 'Queuing…'
                          : 'Publish now'}
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        <DataTable
          component={Paper}
          loading={loading}
          empty={!posts.length}
          emptyMessage="No scheduled posts in the queue."
        >
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Platform Account</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.map((post) => {
              const err = getPostPublishError(post);
              return (
                <TableRow key={post.id}>
                  <TableCell>{post.id}</TableCell>
                  <TableCell>{post.platform_account_id}</TableCell>
                  <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>
                    {post.content}
                  </TableCell>
                  <TableCell>{post.content_type}</TableCell>
                  <TableCell>
                    {formatSchedulingTime(post.scheduled_time)}
                  </TableCell>
                  <TableCell>{renderStatusCell(post)}</TableCell>
                  <TableCell>
                    {err ? (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ maxWidth: 240, display: 'block' }}
                      >
                        {err}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canPublishNow(post) && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PublishIcon />}
                        disabled={publishingId === post.id}
                        onClick={() => handlePublishNow(post)}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Publish now
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DataTable>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule a New Post</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              label="Platform Account ID"
              name="platform_account_id"
              value={form.platform_account_id}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Content"
              name="content"
              value={form.content}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              minRows={3}
              required
            />
            <TextField
              label="Content Type"
              name="content_type"
              value={form.content_type}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Scheduled Time (YYYY-MM-DDTHH:MM)"
              name="scheduled_time"
              value={form.scheduled_time}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              placeholder="2024-07-07T15:00"
            />
            <TextField
              label="Timezone"
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting ? 'Scheduling...' : 'Schedule'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
